import React, { useRef, useState, useEffect } from 'react';

import { VerifyCodeWrapper, BoxInput } from './VerifyCode.styles';

type VerifyCodeProps = {
  theme: 'dark' | 'light';
  width: string | number;
  height: string | number;
  numberOfBoxes: number;
  onChangeValue: (value: string) => void;
  value?: string;
  isError?: boolean;
  isValid?: boolean;
};

const VerifyCode: React.FC<VerifyCodeProps> = ({
  theme,
  width,
  height,
  numberOfBoxes,
  value,
  onChangeValue,
  isError: isErrorProp,
  isValid,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [values, setValues] = useState<string[]>(
    new Array(numberOfBoxes).fill('')
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const newCharsFromProp = new Array(numberOfBoxes).fill('');
    for (let i = 0; i < numberOfBoxes; i++) {
      newCharsFromProp[i] = (value || '')[i] || '';
    }
    if (newCharsFromProp.join('') !== values.join('')) {
      setValues(newCharsFromProp);
    }
  }, [value, numberOfBoxes]);

  useEffect(() => {
    if (isErrorProp) {
      setHasError(true);
    }
  }, [isErrorProp]);

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value.replace(/[^0-9]/g, '');
    if (!newValue) return;

    if (newValue && index < numberOfBoxes - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const updatedValues = [...values];
    updatedValues[index] = newValue;
    setValues(updatedValues);
    onChangeValue(updatedValues.join(''));
    setHasError(false);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const updatedValues = [...values];

      if (values[index]) {
        updatedValues[index] = '';
      } else if (index > 0) {
        updatedValues[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }

      setValues(updatedValues);
      onChangeValue(updatedValues.join(''));
      setHasError(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData
      .getData('Text')
      .slice(0, numberOfBoxes);
    if (!pastedData) return;

    const newValues = pastedData.split('').slice(0, numberOfBoxes);
    setValues(newValues);
    onChangeValue(newValues.join(''));
    setHasError(false);

    inputRefs.current[Math.min(newValues.length, numberOfBoxes) - 1]?.focus();
  };

  return (
    <VerifyCodeWrapper
      $width={width}
      $height={height}
      $numberOfBoxes={numberOfBoxes}
      $isError={hasError}
    >
      {Array.from({ length: numberOfBoxes }).map((_, index) => {
        return (
          <BoxInput
            $theme={theme}
            key={index}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            placeholder="-"
            maxLength={1}
            value={values[index]}
            onChange={event => {
              return handleChange(index, event);
            }}
            onKeyDown={event => {
              return handleKeyDown(index, event);
            }}
            onPaste={handlePaste}
            inputMode="numeric"
            pattern="\d*"
            $isError={hasError}
            $isValid={isValid}
            $filled={values[index].length > 0}
          />
        );
      })}
    </VerifyCodeWrapper>
  );
};

export default VerifyCode;
